/**
 * Copyright 2026 Bluecircle Technologies Pvt Limited. All rights reserved.
 *
 * This software and its source code are the proprietary and confidential
 * intellectual property of Bluecircle Technologies Pvt Limited. Unauthorized
 * copying, reproduction, distribution, modification, public display, or use
 * of this software, in whole or in part, is strictly prohibited without the
 * prior written permission of Bluecircle Technologies Pvt Limited.
 */

/**
 * A workspace's setup as members see it (openspec add-account-workspace-setup 4.4, 4.5): the
 * reply of `GET /api/knet/setup/:wsId`, shared by the route and the workspace page.
 */

/**
 * `stale`: the run must run again (the owner's plan changed, or knetai lost a run in progress);
 * the sweep restarts it (openspec persist-setup-status D4).
 */
export type SetupRunPhase = "not_started" | "setting_up" | "ready" | "failed" | "stale";

/** One step's outcome; `at` (ISO time of the answer) is set on steps KnetDB recorded. */
export interface SetupStepResult { step: string; state: string; reason?: string; at?: string }

export interface WorkspaceSetupView {
  state: SetupRunPhase;
  /** The step running now, or the step the run stopped at. */
  step: string | null;
  steps: SetupStepResult[];
  /** True for an owner when there is something to (re)start: failed, stale or never started. */
  canRetry: boolean;
  /** True for an owner of a ready workspace: Re-check setup starts the run again. */
  canRecheck: boolean;
}

/** A step entry as KnetDB records it: always timed. */
export interface RecordedStep extends SetupStepResult { at: string }

/**
 * Merge step answers into recorded steps (persist-setup-status D2): per step, the entry with the
 * later `at` wins; equal times fall back to comparing state and reason, so the result does not
 * depend on the order or number of merges. The result lists steps in setup order.
 */
export function mergeSteps(recorded: RecordedStep[], answers: RecordedStep[]): RecordedStep[] {
  const byStep = new Map<string, RecordedStep>();
  for (const entry of [...recorded, ...answers]) {
    const s = { ...entry, step: resolveStepId(entry.step) };
    const prev = byStep.get(s.step);
    if (!prev || compareEntries(s, prev) > 0) { byStep.set(s.step, s); }
  }
  return [...byStep.values()].sort((a, b) => stepOrder(a.step) - stepOrder(b.step) || cmp(a.step, b.step));
}

function compareEntries(a: RecordedStep, b: RecordedStep): number {
  return cmp(a.at, b.at) || cmp(a.state, b.state) || cmp(a.reason ?? "", b.reason ?? "");
}

function cmp(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

function stepOrder(step: string): number {
  const i = Object.keys(SETUP_STEP_LABELS).indexOf(step);
  return i === -1 ? Number.MAX_SAFE_INTEGER : i;
}

/**
 * The banner's rows (persist-setup-status "The banner shows each service's setup state"): every
 * workspace step once, with its recorded state or `pending`, then any other recorded step. While
 * the account's billing runs (`step` is `billing`) the rows are the account run's own steps.
 */
export function setupStepRows(view: WorkspaceSetupView): SetupStepResult[] {
  if (view.step === "billing") { return view.steps; }
  const recorded = new Map(view.steps.map(s => [s.step, s]));
  const rows: SetupStepResult[] = WORKSPACE_SETUP_STEPS.map(step => recorded.get(step) ?? { step, state: "pending" });
  for (const s of view.steps) {
    if (!WORKSPACE_SETUP_STEPS.includes(s.step)) { rows.push(s); }
  }
  return rows;
}

/**
 * Display names of WorkspaceSetup's steps, in run order (and AccountSetup's `billing`). A step is
 * named after the service it calls, `knet-<service>` without the prefix; `db` and `agents` keep
 * their short names (openspec use-service-names-in-setup).
 */
export const SETUP_STEP_LABELS: Record<string, string> = {
  billing: "Billing",
  db: "KNet DB",
  connectors: "KNet Connectors",
  flows: "KNet Flows",
  // knet-flows openspec onboarding-channels-before-kue: Channels sets up before Kue, which registers its agent there.
  channels: "KNet Channels",
  kue: "Kue",
  traces: "KNet Traces",
  knowledgebases: "KNet Knowledge Bases",
  crawlers: "KNet Crawlers",
  voices: "KNet Voices",
  chat: "KNet Chat",
  maps: "KNet Maps",
  whatsapp: "KNet WhatsApp",
  agents: "KNet Agents",
  crawl: "KNet Crawl",
};

/**
 * Step ids before use-service-names-in-setup, and their new ids. Runs started with the old K-Net
 * still send them, and rows written before the migration still hold them.
 */
export const LEGACY_STEP_IDS: Readonly<Record<string, string>> = {
  aci: "connectors",
  knetai: "flows",
  kb: "knowledgebases",
  krawler: "crawlers",
  voice: "voices",
  map: "maps",
  wa: "whatsapp",
};

/** A step's current id: an old id maps to its new one; any other id is returned as it is. */
export function resolveStepId(step: string): string {
  return Object.prototype.hasOwnProperty.call(LEGACY_STEP_IDS, step) ? LEGACY_STEP_IDS[step] : step;
}

export function setupStepLabel(step: string): string {
  return SETUP_STEP_LABELS[resolveStepId(step)] ?? step;
}

/**
 * Whether the workspace page shows the setup banner: while setup runs, when it failed or must run
 * again, or when an owner can start it. A ready workspace (or one whose setup isn't known) shows
 * nothing; its owner gets only the Re-check link.
 */
export function showSetupBanner(view: WorkspaceSetupView | null): boolean {
  if (!view) { return false; }
  return view.state === "setting_up" || view.state === "failed" || view.state === "stale" || view.canRetry;
}

/** WorkspaceSetup's steps (every label but AccountSetup's `billing`). */
export const WORKSPACE_SETUP_STEPS = Object.keys(SETUP_STEP_LABELS).filter(s => s !== "billing");

/** Rows finished (ready, or skipped as not in plan), for "n of m ready". */
export function finishedSteps(view: WorkspaceSetupView): number {
  return setupStepRows(view).filter(s => s.state === "ready" || s.state === "skipped").length;
}

/** The "m" of "n of m ready": the banner's rows, so every workspace step counts. */
export function totalSteps(view: WorkspaceSetupView): number {
  return setupStepRows(view).length;
}
